<template>
  <div class="max-w-xl w-full mx-auto">
    <div class="mt-8 mx-auto px-4">
      <div class="bg-white shadow rounded-lg p-6">
        <h1 class="text-2xl font-extrabold text-gray-900 mb-2">
          {{ resolvedTitle }}
        </h1>
        <div v-if="isCustom" class="space-y-3 text-gray-700 mb-4">
          <p class="whitespace-pre-line">{{ resolvedBody }}</p>
        </div>
        <div v-else class="space-y-3 text-gray-700 mb-4">
          <p>
            At UCD Development and Alumni Relations Office (DARO), we are committed to staying connected with our alumni community. To engage with you effectively and respectfully, we kindly ask for your consent to the following:
          </p>
          <ol class="list-decimal pl-6 space-y-2">
            <li>
              <span class="font-semibold">Use of Your Updated Personal Data:</span>
              I consent to UCD DARO collecting, processing, and storing my updated personal data (e.g., contact details) in accordance with the
              <a :href="privacyUrl || '#'" class="underline text-blue-700" target="_blank" rel="noopener" @click="trackUcdPrivacyLink">UCD DARO Privacy Policy</a>.
              This enables UCD to provide tailored communications, event invitations, and opportunities to engage with UCD’s alumni network.
            </li>
            <li>
              <span class="font-semibold">Use of Facial Recognition Technology:</span>
              I consent to UCD DARO using facial recognition technology for the sole purpose of retrieving and matching my professional headshot and any other relevant images of me from my graduation with my submitted file photograph.
            </li>
          </ol>

          <p>
            In order to process your graduation images, we will share your name, degree and email with My Graduation Clip, a UK-based company which will process your images for the sole purpose of retrieving your graduation clip and/or headshot image using a specialist AI sorting and retrieval tool. Your shared information will be used for this sole purpose, held securely in an encrypted format and deleted within a maximum of 1 year.
          </p>

          <div>
            <p class="font-semibold mb-1">Your GDPR Rights (Articles 12–21):</p>
            <ul class="list-disc pl-6 space-y-1">
              <li>Access, rectify, or erase your personal data.</li>
              <li>Restrict or object to its processing.</li>
              <li>Data portability.</li>
              <li>Withdraw consent at any time (without affecting the lawfulness of prior processing).</li>
              <li>Launch a complaint with a supervisory authority.</li>
            </ul>
          </div>

          <p>
            To exercise these rights or for further details, please contact
            <a :href="`mailto:${contactEmail}`" class="underline text-blue-700">{{ contactEmail }}</a>
            or refer to the
            <a :href="privacyUrl || '#'" class="underline text-blue-700" target="_blank" rel="noopener">UCD DARO Privacy Policy</a>.
            Your consent is voluntary, and you may withdraw it at any time by contacting us.
          </p>
        </div>

        <div v-if="isCustom" class="space-y-3 mb-4">
          <label class="flex items-start space-x-3">
            <input
              type="checkbox"
              v-model="consentCustom"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span class="text-sm text-gray-800">
              {{ resolvedCheckboxText }}
            </span>
          </label>
        </div>
        <div v-else class="space-y-3 mb-4">
          <label class="flex items-start space-x-3">
            <input
              type="checkbox"
              v-model="consentPersonalData"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span class="text-sm text-gray-800">
              I agree to the use of my updated personal data as described above.
            </span>
          </label>
          <label class="flex items-start space-x-3">
            <input
              type="checkbox"
              v-model="consentFacialRecognition"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span class="text-sm text-gray-800">
              I agree to the use of facial recognition technology to retrieve my headshot as described above.
            </span>
          </label>
        </div>

        <div class="flex items-center justify-between">
          <router-link
            to="/privacy-policy"
            class="text-sm text-blue-700 underline"
            @click.native="trackServicePrivacyLink"
          >
            View service privacy policy
          </router-link>
          <button
            type="button"
            class="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            :disabled="!canContinue"
            @click="$emit('accepted')"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from "../../Analytics.vue";
export default {
  name: 'ConsentForm',
  props: {
    mode: { type: String, default: 'ucd_daro' },
    title: { type: String, default: '' },
    body: { type: String, default: '' },
    checkboxText: { type: String, default: '' },
    privacyUrl: { type: String, default: '' },
    contactEmail: { type: String, default: 'alumni@ucd.ie' },
  },
  data() {
    return {
      consentCustom: false,
      consentPersonalData: false,
      consentFacialRecognition: false,
    }
  },
  computed: {
    isCustom() {
      return this.mode === 'custom';
    },
    resolvedTitle() {
      if (this.isCustom) return this.title || 'Consent';
      return 'UCD DARO Consent';
    },
    resolvedBody() {
      return this.body || 'Please confirm that you consent to the processing described for this photo search.';
    },
    resolvedCheckboxText() {
      return this.checkboxText || 'I agree to the consent terms above.';
    },
    canContinue() {
      if (this.isCustom) return this.consentCustom;
      return this.consentPersonalData && this.consentFacialRecognition;
    }
  },
  methods: {
    trackUcdPrivacyLink() {
      try {
        trackEvent(this.$route.path, { label: 'SNAPSHOT_CONSENT_UCD_PRIVACY_LINK' });
      } catch (_) { return; }
    },
    trackServicePrivacyLink() {
      try {
        trackEvent(this.$route.path, { label: 'SNAPSHOT_CONSENT_SERVICE_PRIVACY_LINK' });
      } catch (_) { return; }
    },
  }
}
</script>

<style scoped>
.disabled\:opacity-50:disabled { opacity: 0.5; }
</style>
